import {Component, EventEmitter, Input, OnChanges, Output, SimpleChange, SimpleChanges} from '@angular/core';
import {AsyncPipe, NgForOf, NgIf} from "@angular/common";
import {FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators} from "@angular/forms";
import {combineLatest, filter, Observable, of, Subscription, take} from "rxjs";
import {ReportService} from "../../../../../services/report.service";
import {PopupService} from "../../../../../services/popup.service";
import {Store} from "@ngrx/store";
import {ReportState} from "../../../../../state/report/report.reducer";
import { selectRefineIcpScoreFailure, selectRefineIcpScoreSuccess, selectShareReportFailure, selectShareReportSuccess } from "../../../../../state/report/report.selectors";
import {map} from "rxjs/operators";
import {refineIcpScore, updateCompany} from '../../../../../state/report/report.actions'
import {updateCompanyIcpScore} from "../../../../../utils/update-company-icp-score.util";
import {Company} from "../../../../../interfaces/company.interface";

@Component({
  selector: 'app-icp-score',
  standalone: true,
  imports: [
    AsyncPipe,
    FormsModule,
    NgIf,
    ReactiveFormsModule,
    NgForOf
  ],
  templateUrl: './icp-score.component.html',
  styleUrl: './icp-score.component.scss'
})
export class IcpScoreComponent implements OnChanges {

  @Input() reportSearch: any;
  @Input() company: Company;

  @Input() isVisible: boolean = false;
  @Output() modalClosed: EventEmitter<void> = new EventEmitter();
  @Output() icpScoreUpdated = new EventEmitter<number>();

  form: FormGroup;
  loading$: Observable<boolean>;

  // Add a new observable to handle the combined logic
  isSubmitDisabled$: Observable<boolean>;

  // Array to hold the questions and their options (using camelCase IDs)
  questions = [
    { id: 'inboundLead', text: 'Is the contact an inbound lead?' },
    { id: 'confirmedNeed', text: 'Has the contact confirmed a need?' },
    { id: 'validateNeed', text: 'Can you validate that the business need can be fulfilled with the products/services that you sell?' },
    { id: 'existingCustomer', text: 'Is this an existing customer already?' },
    { id: 'partnerEngaged', text: 'Is there an existing partner engaged already?' },
    { id: 'competitiveProduct', text: 'Replacing a target competitive product?' },
    { id: 'budget', text: 'Is there a budget?' },
    { id: 'acceptAdvise', text: 'Can you confirm that the customer seeks and accepts advise?' },
    { id: 'senseUrgency', text: 'Can you confirm that there is a sense of urgency?' }
  ];

  // Object to hold selected values for each question, defaulting to "N/A"
  selectedValues: { [key: string]: string } = {
    inboundLead: 'na',
    confirmedNeed: 'na',
    validateNeed: 'na',
    existingCustomer: 'na',
    partnerEngaged: 'na',
    competitiveProduct: 'na',
    budget: 'na',
    acceptAdvise: 'na',
    senseUrgency: 'na'
  };

  constructor(
    private formBuilder: FormBuilder,
    private reportService: ReportService,
    private store: Store<{ report: ReportState }>,
    private popupService: PopupService
  ) {
    // Initialize all selected values to "N/A" by default
    this.questions.forEach(q => {
      this.selectedValues[q.id] = 'na';  // Default each question to N/A
    });

    this.form = this.formBuilder.group({
      acceptAdviseDetails: [''],  // Field for advise details
      urgencyDetails: ['']         // Field for urgency details
    });

    this.loading$ = this.store.select(state => state.report.loading);

    // Combine loading$ and form.invalid into a single observable
    this.isSubmitDisabled$ = combineLatest([
      this.loading$,
      of(this.form.invalid)  // Convert form.invalid to an observable
    ]).pipe(
      map(([loading, formInvalid]) => loading || formInvalid)  // Disable if loading or form is invalid
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    // console.log('ngOnChanges icp-score.component.ts');

    if (changes['company']) {
      const companyChange: SimpleChange = changes['company'];

      // Check if icp_score has changed
      const previousIcpScore = companyChange.previousValue?.icp_score;
      const currentIcpScore = companyChange.currentValue?.icp_score;

      if (previousIcpScore !== currentIcpScore) {
        // console.log('icp_score has changed:', currentIcpScore);

        const company = companyChange.currentValue;
        this.populateIcp(company);
      } else {
        // console.log('icp_score has not changed.');
      }
    }
  }

  populateIcp(company: any) {
    // console.log('[icp-score.component.ts] populateIcp')

    /*
    const currentCompany = this.reportSearch.search_companies.find(
      (c: any) => c.id === company.id
    );
    */

    const icpDetail = company.search_company_icp_detail;

    if (icpDetail) {
      const mapValue = (value: number) => {
        if (value === 1) return 'yes';
        if (value === 0) return 'no';
        return 'na'; // Default to 'na' for -1 or other values
      };

      this.selectedValues = {
        inboundLead: mapValue(icpDetail.contact_inboud_lead),
        confirmedNeed: mapValue(icpDetail.contact_confirmed_need),
        validateNeed: mapValue(icpDetail.contact_validated_need),
        existingCustomer: mapValue(icpDetail.existing_customer),
        partnerEngaged: mapValue(icpDetail.existing_partner),
        competitiveProduct: mapValue(icpDetail.competitive_product),
        budget: mapValue(icpDetail.budget),
        acceptAdvise: mapValue(icpDetail.accept_advise),
        senseUrgency: mapValue(icpDetail.urgency),
      };

      // Populate form controls if data exists
      this.form.patchValue({
        acceptAdviseDetails: icpDetail.adivce_details || '', // Populate or set to empty
        urgencyDetails: icpDetail.urgency_details || ''     // Populate or set to empty
      });
    }

  }

  closeModal() {
    this.isVisible = false;
    this.modalClosed.emit(); // Notify the parent component
    // this.form.reset(); // Reset the form
  }

  // Method to handle the selection of values for each question
  selectAnswer(questionId: string, value: string) {
    // Update the selected value
    this.selectedValues = { ...this.selectedValues, [questionId]: value };

    // Handle validation for specific fields
    if (questionId === 'acceptAdvise') {
      if (value === 'yes') {
        this.form.get('acceptAdviseDetails')?.setValidators([Validators.required]);
      } else {
        this.form.get('acceptAdviseDetails')?.clearValidators();
        this.form.get('acceptAdviseDetails')?.reset();
      }
      this.form.get('acceptAdviseDetails')?.updateValueAndValidity();
    }

    if (questionId === 'senseUrgency') {
      if (value === 'yes') {
        this.form.get('urgencyDetails')?.setValidators([Validators.required]);
      } else {
        this.form.get('urgencyDetails')?.clearValidators();
        this.form.get('urgencyDetails')?.reset();
      }
      this.form.get('urgencyDetails')?.updateValueAndValidity();
    }
  }

  // Prevent submission if the form is invalid
  refineIcpScore() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();  // Show validation errors
      return;
    }

    const searchCompanyId = this.company.id;
    const selectedValues = this.selectedValues;
    const formValues = this.form.value;

    // Dispatch the action to refine the ICP score
    this.store.dispatch(refineIcpScore({ searchCompanyId, selectedValues, formValues }));

    const refineIcpScoreSuccess$ = this.store.select(selectRefineIcpScoreSuccess);
    const refineIcpScoreFailure$ = this.store.select(selectRefineIcpScoreFailure);

    let refineIcpScoreSuccessSubscription: Subscription = null;
    let refineIcpScoreFailureSubscription: Subscription = null;

    refineIcpScoreSuccessSubscription = refineIcpScoreSuccess$.pipe(
      filter((result) => result !== null),
      //take(1)
    ).subscribe({
      next: (result) => {
        console.log('ICP Score refinement successful:', result);

        if (result) {

          // console.log('result', result)

          // Emit the new icp_score from the response
          const newIcpScore = result.searchCompany.icp_score;
          const searchCompanyIcpDetail = result.searchCompany.searchCompanyIcpDetail

          // this.icpScoreUpdated.emit(newIcpScore); // Emit the new score

          // update icp score inside company
          const updatedCompany = updateCompanyIcpScore(this.company, newIcpScore, searchCompanyIcpDetail);
          if (updatedCompany) {
            // Dispatch an action or update the state
            this.store.dispatch(updateCompany({ company: updatedCompany }));
            // console.log('Updated company:', updatedCompany);
          } else {
            console.error('Failed to update the company.');
          }

          this.closeModal();
          this.popupService.notify('ICP Score refined successfully!', 'Success', 'success', 2500);

          if (refineIcpScoreSuccessSubscription) {
            refineIcpScoreSuccessSubscription.unsubscribe();
          }

        }
      },
      error: (error) => {
        console.error('ICP Score success subscription error:', error);

        if (refineIcpScoreSuccessSubscription) {
          refineIcpScoreSuccessSubscription.unsubscribe();
        }
      }
    });

    refineIcpScoreFailureSubscription = refineIcpScoreFailure$.pipe(
      filter((error) => error !== null),
      take(1)
    ).subscribe({
      next: (error) => {
        const errorMessage = error.error.message || 'Failed to refine ICP Score.';
        this.popupService.notify(errorMessage, 'Error', 'error');

        if (refineIcpScoreFailureSubscription) {
          refineIcpScoreFailureSubscription.unsubscribe();
        }
      },
      error: (error) => {
        console.error('ICP Score failure subscription error:', error);

        if (refineIcpScoreFailureSubscription) {
          refineIcpScoreFailureSubscription.unsubscribe();
        }
      }
    });
  }

}
